import React from 'react';

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddRemoveIcon from '@sharedCR/common/AddRemoveIcon';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { COLOR_PALETTE } from 'constants/ui';

import { LoanFeesTestIds } from '../../../../../LoanFees.testIds';
import { addButtonStyles } from '../../AccordionDetailsView/FeeTable/FeeTable.styles';

import { ADJUSTMENTS_FORM_DATASET_NAME } from './AdjustmentsTable.constants';
import { adjustmentsTableStyles } from './AdjustmentsTable.styles';
import AdjustmentsTableRow from './AdjustmentsTableRow';

const AdjustmentsTable = ({
  data,
  isWithHeader,
  onAdd,
  onDelete,
  setIsLoading,
  tableTitle,
  adjustmentTypeOptions,
  adjustmentTypeOptionsNotDeleted,
  isLoading,
  meta: { handleChange, validateField, validateForm, setInForm, setForm, validationErrors },
}) => {
  return (
    <TableContainer sx={adjustmentsTableStyles}>
      <Typography className="table-title" component="p" variant="bold16">
        {tableTitle}
      </Typography>

      <Table>
        {isWithHeader && (
          <TableHead>
            <TableRow className="table-header">
              <TableCell>Adjustment Type</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
        )}

        <TableBody>
          {data.map(({ adjustmentId, ...adjustment }, index) => {
            return (
              <AdjustmentsTableRow
                {...adjustment}
                adjustmentId={adjustmentId}
                adjustmentTypeOptions={adjustmentTypeOptions}
                adjustmentTypeOptionsNotDeleted={adjustmentTypeOptionsNotDeleted}
                formDatasetName={ADJUSTMENTS_FORM_DATASET_NAME}
                handleChange={handleChange}
                index={index}
                isLoading={isLoading}
                key={adjustmentId}
                pendingChangesActionType={adjustment.pendingChangesActionType}
                setForm={setForm}
                setInForm={setInForm}
                setIsLoading={setIsLoading}
                validateField={validateField}
                validateForm={validateForm}
                validationErrors={validationErrors}
                onDelete={onDelete}
              />
            );
          })}
        </TableBody>
      </Table>

      <div className="flex justify-start my-12">
        <Button
          data-testid={LoanFeesTestIds.ADJUSTMENTS_ADD_BUTTON}
          disabled={isLoading}
          sx={addButtonStyles}
          variant="text"
          onClick={onAdd}
        >
          <AddRemoveIcon color={COLOR_PALETTE.BRAND[6]} />
          <Typography variant="regular14">Add Adjustment</Typography>
        </Button>
      </div>
    </TableContainer>
  );
};

AdjustmentsTable.propTypes = {
  ...WithLoadingPropTypes,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      pendingChangesActionType: PropTypes.string,
      applicationSellerAdjustmentId: PropTypes.string,
      adjustmentId: PropTypes.string,
      modifiedFields: PropTypes.arrayOf(PropTypes.string),
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      sellerAdjustmentName: PropTypes.string,
      sellerAdjustmentTypeId: PropTypes.shape({
        name: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        isDeleted: PropTypes.bool.isRequired,
      }),
      initialFee: PropTypes.object,
    }),
  ).isRequired,
  isWithHeader: PropTypes.bool.isRequired,
  adjustmentTypeOptions: PropTypes.array.isRequired,
  adjustmentTypeOptionsNotDeleted: PropTypes.array.isRequired,
  meta: PropTypes.shape({
    handleChange: PropTypes.func.isRequired,
    validateField: PropTypes.func.isRequired,
    validateForm: PropTypes.func.isRequired,
    setInForm: PropTypes.func.isRequired,
    setForm: PropTypes.func.isRequired,
    setIsTouched: PropTypes.func.isRequired,
    validationErrors: PropTypes.object.isRequired,
    setEditedSection: PropTypes.func.isRequired,
  }).isRequired,
  tableTitle: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default AdjustmentsTable;
